// ------------------------------------------------------------------------------
// name: currency
// author: mudas
// created: 2022/5/27
// ------------------------------------------------------------------------------

/**
 * formatCurrency 的 options 参数类型
 */
export interface CurrencyOptions extends Omit<Intl.NumberFormatOptions, 'style' | 'currency'> {
  /**
   * 货币符号类型（默认：CNY）
   * - 参考：https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes
   */
  currency?: boolean | string;

  /**
   * 当转换结果为 0 时，是否替换输出结果
   */
  replacer?: string;

  /**
   * 指定小数位数（默认：2 位）
   * @deprecated 不再推荐使用，请改用 maximumFractionDigits 属性
   */
  precision?: number;

  /**
   * 是否保留小数中尾随的 0（默认：false - 不保留）
   *
   * @example
   * 2.00 会转换为 '2'
   * 2.10 会转换为 '2.1'
   */
  trailing?: boolean;
}

const parseDigits = (options?: CurrencyOptions, defaultValue: number = 2) => {
  const { precision, maximumFractionDigits } = options || {};

  if (precision !== undefined) {
    return precision;
  }
  else if (maximumFractionDigits !== undefined) {
    return maximumFractionDigits;
  }

  return defaultValue;
};

/**
 * 格式化货币数据为千分位+小数位
 * @param value
 * @param options
 *
 * @see CurrencyOptions
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
 */
export const formatCurrency = (value: number | string, options?: CurrencyOptions): string => {
  const { currency = true, replacer, precision, trailing, ...opt } = options || {};

  const truthValue = !!value ? +value : 0;
  const digits = parseDigits(options, 2); // 最大小数位数，默认 2 位
  const formattedOptions = {
    style: currency ? 'currency' : 'decimal',
    currency: currency ? (currency === true ? 'CNY' : currency) : undefined,
    currencyDisplay: 'narrowSymbol',

    // 最小小数位数
    minimumFractionDigits: trailing ? digits : 0,

    // 最大小数位数
    maximumFractionDigits: digits,

    // 参数覆盖，满足更多自定义功能
    ...opt
  };

  return replacer && truthValue === 0 ? replacer : Intl.NumberFormat(undefined, formattedOptions).format(truthValue);
};
